// Company profile photos tab. A company admin uploads images one at a time;
// the server processes each synchronously (auto-orient → resize → WebP, no
// watermark) and returns the refreshed list, so — unlike the tournament photos
// tab — there is nothing to poll. Without MANAGE_PHOTOS the section is a
// read-only gallery (the server refuses the mutations regardless).

import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { COMPANY_PHOTO_MAX, type CompanyPhoto } from '../domain';
import {
  deleteCompanyPhoto,
  fetchCompanyPhotos,
  uploadCompanyPhoto,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { PhotoLightbox, type LightboxPhoto } from './PhotoLightbox';

interface CompanyPhotosSectionProps {
  companyId: number;
}

export function CompanyPhotosSection({ companyId }: CompanyPhotosSectionProps) {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const canManage = hasPermission('MANAGE_PHOTOS');

  const [photos, setPhotos] = useState<CompanyPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Read the translator through a ref so the loader effect does not re-run (and
  // refetch) whenever the language changes.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPhotos(await fetchCompanyPhotos(companyId));
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.companyPhotos.errors.load'),
      );
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onFile = useCallback(
    async (file: File | null) => {
      if (!file) return;
      setUploading(true);
      setError(null);
      try {
        setPhotos(await uploadCompanyPhoto(companyId, file));
      } catch (e: unknown) {
        setError(
          e instanceof Error ? e.message : t('admin.companyPhotos.errors.upload'),
        );
      } finally {
        setUploading(false);
      }
    },
    [companyId, t],
  );

  const onDelete = useCallback(
    async (photoId: string) => {
      setError(null);
      try {
        setPhotos(await deleteCompanyPhoto(companyId, photoId));
      } catch (e: unknown) {
        setError(
          e instanceof Error ? e.message : t('admin.companyPhotos.errors.delete'),
        );
      }
    },
    [companyId, t],
  );

  const atCapacity = photos.length >= COMPANY_PHOTO_MAX;

  const lightboxPhotos: LightboxPhoto[] = photos.map((p) => ({
    id: p.id,
    url: p.url,
    comment: '',
  }));
  const openLightbox = (photoId: string) => {
    const i = lightboxPhotos.findIndex((p) => p.id === photoId);
    if (i >= 0) setLightboxIndex(i);
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('admin.companyPhotos.title')}
        </Typography>
        <Chip
          variant="outlined"
          label={`${photos.length} / ${COMPANY_PHOTO_MAX}`}
        />
        {canManage && (
          <Tooltip
            title={
              atCapacity
                ? t('admin.companyPhotos.atCapacity', { count: COMPANY_PHOTO_MAX })
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                component="label"
                startIcon={
                  uploading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                disabled={uploading || atCapacity}
              >
                {t('admin.companyPhotos.add')}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    e.target.value = ''; // allow re-selecting the same file
                    void onFile(file);
                  }}
                />
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {canManage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.companyPhotos.hint', { max: COMPANY_PHOTO_MAX })}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && photos.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : photos.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
          <CollectionsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {t('admin.companyPhotos.empty')}
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
          }}
        >
          {photos.map((photo) => (
            <Paper
              key={photo.id}
              variant="outlined"
              sx={{ position: 'relative', overflow: 'hidden' }}
            >
              <Box
                component="img"
                src={photo.url}
                alt={t('admin.companyPhotos.alt')}
                loading="lazy"
                onClick={() => openLightbox(photo.id)}
                sx={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  objectFit: 'cover',
                  display: 'block',
                  cursor: 'zoom-in',
                }}
              />
              {canManage && (
                <IconButton
                  size="small"
                  aria-label={t('admin.companyPhotos.deletePhoto')}
                  onClick={() => void onDelete(photo.id)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(0,0,0,0.45)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
          ))}
        </Box>
      )}

      <PhotoLightbox
        photos={lightboxPhotos}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        showDownload
        labels={{
          close: t('gallery.close'),
          download: t('gallery.download'),
          previous: t('gallery.previous'),
          next: t('gallery.next'),
          alt: t('admin.companyPhotos.alt'),
        }}
      />
    </Paper>
  );
}
